/**
 * Where the browser sends PostHog traffic.
 *
 * The default — a `/ingest` path on the app's own origin — survives ad
 * blockers, and that is why it exists. What it also does is put every event
 * and every session-replay chunk on the SAME origin as the app: one HTTP/2
 * connection carries the RSC navigations, the manifest, the images AND the
 * telemetry. They then share a fate. A stalled replay upload does not merely
 * fail to record a session; it sits in the same connection as the RSC fetch
 * for the page the user is trying to open, and the page hangs with it. That
 * is the "everything pending for 40 seconds, then a mass cancel" shape.
 *
 * Setting `NEXT_PUBLIC_POSTHOG_INGEST_ORIGIN` to a dedicated host on the same
 * registrable domain (`https://buzz.hiive.club`) keeps both properties: still
 * first-party for cookies and still invisible to blocklists that only know
 * `posthog.com`, but on its own origin, so its connection cannot be the one
 * the app is waiting on. `next.config.ts` proxies that host to PostHog.
 *
 * Read through a static `process.env.X` reference so Next inlines it into the
 * client bundle — a dynamic lookup returns undefined in the browser.
 */

const SAME_ORIGIN_INGEST_PATH = "/ingest";

export function resolvePostHogIngestOrigin(): string {
  const configured = process.env.NEXT_PUBLIC_POSTHOG_INGEST_ORIGIN?.trim();
  if (!configured) return SAME_ORIGIN_INGEST_PATH;

  // A trailing slash makes posthog-js build `https://buzz.hiive.club//i/v0/e/`,
  // which the proxy forwards verbatim and PostHog 404s.
  const withoutTrailingSlash = configured.replace(/\/+$/, "");
  return withoutTrailingSlash.length > 0
    ? withoutTrailingSlash
    : SAME_ORIGIN_INGEST_PATH;
}

/**
 * The hostname `next.config.ts` scopes its proxy rewrites to, or `null` when
 * ingestion is still riding the app origin.
 *
 * The rewrite this feeds is a `beforeFiles` catch-all on `/:path*`, so a
 * hostname that also serves the app would route every page, every RSC payload
 * and every asset to PostHog. That is a whole-site outage from one typo in an
 * environment variable, so the app's own host is refused here rather than
 * anywhere later.
 */
export function resolvePostHogIngestHostname(): string | null {
  const origin = process.env.NEXT_PUBLIC_POSTHOG_INGEST_ORIGIN?.trim();
  if (!origin) return null;

  let hostname: string;
  try {
    hostname = new URL(origin).hostname;
  } catch {
    return null;
  }
  if (!hostname) return null;

  const appHost = process.env.NEXT_PUBLIC_APP_URL?.trim();
  if (appHost) {
    try {
      if (new URL(appHost).hostname === hostname) return null;
    } catch {
      // An unparseable app URL is not evidence of a collision.
    }
  }

  return hostname;
}

/** Assets (the replay recorder, the toolbar) come from PostHog's asset CDN. */
export function postHogAssetOrigin(): string {
  const host = process.env.NEXT_PUBLIC_POSTHOG_HOST;
  return host?.includes("eu")
    ? "https://eu-assets.i.posthog.com"
    : "https://us-assets.i.posthog.com";
}

/** The regional ingestion endpoint both the proxy and posthog-node target. */
export function postHogIngestionApiOrigin(): string {
  const host = process.env.NEXT_PUBLIC_POSTHOG_HOST;
  return host?.includes("eu.posthog.com")
    ? "https://eu.i.posthog.com"
    : "https://us.i.posthog.com";
}
